import { CAMPAIGN_NAME_MAXIMUM } from "../campaign/name-rule.js";
import { WEB_ORIGIN_MAX_LENGTH } from "../agent/web-origin.js";
import { parseFieldValue } from "./fill-value.js";
// ── the kinds ───────────────────────────────────────────────────────────────────────────────
/** What a registered field may hold. Every kind has a parser (`parseFillValue`) and a wire shape ({@link ValueOfKind}). */
export const FILLABLE_KIND = [
    "text",
    "single_choice",
    "multi_choice",
    "local_time",
    "local_date",
    "whole_number",
    "day_set",
    /** AMENDED 2026-10-18: a yes/no control (a checkbox, a switch) — a boolean. */
    "toggle",
    /** AMENDED 2026-10-18: the opening-hours editor's change set (`./opening-hours-fill.ts`). */
    "opening_hours",
];
/**
 * The FR-HA-20 categories — labels for the never-fill map, its conformance test and a refusal's record; never a field
 * kind. Disjoint from {@link FILLABLE_KIND} (a type-level check below and a test).
 */
export const NEVER_FILL_KIND = [
    /** Passwords, wherever they are typed. */
    "password",
    /** TOTP codes and email sign-in codes. */
    "one_time_code",
    "recovery_code",
    /** Card numbers and anything inside the payment provider's hosted page. */
    "card",
    /** IBANs and every other payout detail. */
    "payout_detail",
    "api_key",
    "api_key_scope",
    /** SIP passwords and digest details. */
    "sip_secret",
    /** Webhook signing secrets. */
    "webhook_secret",
    /** Delete, close, archive, cancel and erase confirmations. */
    "deletion",
    /** A typed confirmation phrase. */
    "confirmation_phrase",
    /** Terms acceptance and consent checkboxes. */
    "consent",
    "file_upload",
    /** Role and membership changes, invitations and ownership transfer. */
    "membership",
    /** The organization's legal and billing identity (name, address, tax id). */
    "legal_profile",
    /** Spend caps and workspace budgets. */
    "spend_cap",
    /** Another person's number or address (a do-not-call entry, a contact list's people). */
    "contact_detail",
    /** Sharing between workspaces. */
    "resource_share",
    /** The organization's sign-in policy, allowed networks and single sign-on. */
    "sign_in_policy",
    /** Any field its form marks sensitive. */
    "sensitive",
    // AMENDED 2026-10-18 (owner: component-level change sets; consent-based page reading — ADR 0072): the categories a
    // control can never be actioned in, whatever component draws it. The model may only point at them.
    /** The website address (owner 2026-10-17: it starts a crawl). */
    "website_address",
    /** Pasted or typed information text areas (the wizard's and the Information tab's). */
    "pasted_text",
    /** Publish, build, go live and every final submit. */
    "publish",
    /** Paying, topping up, buying, checking out, buying or cancelling a plan. */
    "purchase",
    /** Anything that moves money, a price or a fee included. */
    "money_movement",
    /** Sending a message, an email or an SMS. */
    "send_message",
    /** Signing out. */
    "sign_out",
];
/** Compile-time proof that no kind is both fillable and never-fill: this line stops compiling if one is. */
const NO_SHARED_KIND = true;
void NO_SHARED_KIND;
/** The four `WAIT_CHOICE` minutes of the campaign form (`routes/campaigns/new-campaign-plan.ts`). */
const WAIT_MINUTE_OPTION = ["30", "240", "1440", "4320"];
/** The campaign form's attempt cards. */
const ATTEMPT_OPTION = ["1", "2", "3"];
const CALLING_WINDOW_FIELD = {
    "name": { kind: "text", maxLength: CAMPAIGN_NAME_MAXIMUM },
    "day_of_week": { kind: "day_set" },
    "from_local_time": { kind: "local_time" },
    "to_local_time": { kind: "local_time" },
    "attempt": { kind: "single_choice", option: ATTEMPT_OPTION },
    "wait_minute": { kind: "single_choice", option: WAIT_MINUTE_OPTION },
    "start_date": { kind: "local_date" },
    "start_time": { kind: "local_time" },
    "end_date": { kind: "local_date" },
};
/** The wizard's Channels step: voice is always on, website chat may join it (`answer.medium`). */
const MEDIUM_OPTION = ["voice", "web_chat"];
export const HELP_FORM_REGISTRY = {
    "new_agent_wizard": {
        routeId: "agents.new",
        path: "/agents/new/$agentTemplateCode",
        capability: "voice_agent.write",
        field: {
            "display_name": { kind: "text", maxLength: 120 },
            // AMENDED 2026-10-18 (owner: "fill the pages by my instructions"): the greeting (Review), the Channels step's
            // channels and lines, and the opening-hours editor (Content, or the Hours step of a template that has one).
            "greeting": { kind: "text", maxLength: 500 },
            "language_code": { kind: "multi_choice" },
            "medium": { kind: "multi_choice", option: MEDIUM_OPTION },
            "channel_unit_count": { kind: "single_choice" },
            "opening_hours": { kind: "opening_hours" },
        },
        componentField: true,
    },
    "campaign_calling_window": {
        routeId: "campaigns.new",
        path: "/campaigns/new",
        capability: "campaign.operate",
        field: CALLING_WINDOW_FIELD,
    },
    "campaign_edit_calling_window": {
        // Reached from the campaign's own page; a navigation cannot name the campaign.
        routeId: null,
        path: "/campaigns/$campaignId/edit",
        capability: "campaign.operate",
        field: CALLING_WINDOW_FIELD,
    },
    // AMENDED 2026-10-18: the agent page's "When it answers" card (tab `channels`) edits the same opening hours; its own
    // "Save hours" builds a new version and stays the person's button.
    "agent_opening_hours": {
        // Reached from the agent's own page; a navigation cannot name the agent.
        routeId: null,
        path: "/agents/$voiceAgentId",
        capability: "voice_agent.write",
        field: {
            "opening_hours": { kind: "opening_hours" },
        },
    },
    "website_widget_origin": {
        routeId: "messaging.web_chat",
        path: "/messaging/$mediumCode",
        // `replaceWebChatWidgetOrigins` admits `channel_binding.write`'s roles; the editor's `canEdit` narrows it further.
        capability: "channel_binding.write",
        field: {
            "typed_origin": { kind: "text", maxLength: WEB_ORIGIN_MAX_LENGTH },
        },
    },
};
// ── the never-fill forms ───────────────────────────────────────────────────────────────────
/**
 * Every form with a field in an FR-HA-20 category, and the categories it holds. Nothing here is fillable, ever: a
 * proposal naming one is refused `never_fill`, a page registers nothing for it, its values never enter a page context.
 * The keys are labels, not {@link HelpFormId}s. Each names the screen it stands for.
 */
export const NEVER_FILL_FORM = {
    // ── signing in and the person's own security ──
    /** `/log-in`: password, email sign-in code, authenticator code. */
    "log_in": ["password", "one_time_code"],
    /** `/sign-up`: password and the terms. */
    "sign_up": ["password", "consent"],
    /** `/join` and `/invitation`: an invited person's password and the terms. */
    "join": ["password", "consent"],
    /** `/forgot-password`, `/reset-password`. */
    "password_reset": ["password", "one_time_code"],
    /** `/me/security`: password change, TOTP set-up, recovery codes, email change. */
    "personal_security": ["password", "one_time_code", "recovery_code"],
    /** The step-up and confirm-your-identity dialogs, wherever they open. */
    "confirm_identity": ["password", "one_time_code", "recovery_code"],
    /** `/organization/security`: sign-in policy, allowed networks, verified domains, single sign-on. */
    "organization_security": ["sign_in_policy", "sensitive"],
    // ── money ──
    /** The payment provider's hosted page and the change-card flow (Finance → Balances and funding). */
    "card_payment": ["card"],
    /** Payouts: the payout destination. */
    "payout_destination": ["payout_detail"],
    /** The organization's legal and billing profile. */
    "legal_profile": ["legal_profile"],
    /** Finance → Allowances and spend caps; the workspace budget. */
    "spend_cap": ["spend_cap"],
    // ── people ──
    /** `/organization/people`: invitations, roles, removal. */
    "organization_people": ["membership"],
    /** A workspace's team: invitations, roles, removal. */
    "workspace_team": ["membership"],
    /** Transferring the organization's ownership. */
    "ownership_transfer": ["membership", "confirmation_phrase"],
    /** `/organization/sharing`: sharing between workspaces. */
    "workspace_sharing": ["resource_share", "membership"],
    /** `/do-not-call`: adding a number. */
    "do_not_call_add": ["contact_detail"],
    /** A contact list's people: adding, replacing, uploading. */
    "contact_list_people": ["contact_detail", "file_upload"],
    // ── keys and secrets ──
    /** Creating or rotating an API key (workspace and organization). */
    "api_key": ["api_key", "api_key_scope"],
    /** The SIP trunk dialog: outbound username and password. */
    "sip_trunk": ["sip_secret"],
    /** A SIP trunk's inbound digest details. */
    "sip_trunk_inbound": ["sip_secret"],
    /** A webhook endpoint and its signing secret. */
    "webhook_endpoint": ["webhook_secret"],
    // ── confirmations ──
    /** Deleting a workspace. */
    "workspace_delete": ["deletion", "confirmation_phrase"],
    /** Archiving or restoring a workspace. */
    "workspace_archive": ["deletion"],
    /** `/organization/archive-close`: closing the organization. */
    "organization_close": ["deletion", "confirmation_phrase"],
    /** Pausing, archiving or deleting a VoiceAgent. */
    "voice_agent_delete": ["deletion", "confirmation_phrase"],
    /** Cancelling a campaign. */
    "campaign_cancel": ["deletion"],
    /** A person-data erasure request. */
    "person_data_erase": ["deletion"],
    /** Every other delete, close, archive, cancel or erase confirmation dialog. */
    "confirmation_dialog": ["deletion", "confirmation_phrase"],
    // ── consent, uploads, anything marked sensitive ──
    /** Terms acceptance and consent checkboxes (the recording consent line, the compliance consent). */
    "consent_terms": ["consent"],
    /** The wizard's and the Information tab's file and pasted-text uploads. */
    "document_upload": ["file_upload"],
    /** Any field its form marks sensitive. */
    "sensitive_field": ["sensitive"],
    // ── AMENDED 2026-10-18 (ADR 0072): the never-actionable categories as screens ──
    /** The wizard's and the Information tab's website address, and "Read the website again". */
    "website_address": ["website_address"],
    /** The wizard's and the Information tab's pasted or typed information. */
    "information_text": ["pasted_text"],
    /** Build, publish, go live, and every final submit of a form. */
    "publish_build": ["publish"],
    /** Top-up, plan purchase or cancellation, checkout, auto-reload, extra time. */
    "purchase": ["purchase", "money_movement"],
    /** Sending a message, an email, an SMS or a test message. */
    "message_send": ["send_message"],
    /** Signing out, on this device or every device. */
    "sign_out": ["sign_out"],
};
/** Compile-time proof that no never-fill form shares an id with a fillable one. */
const NO_SHARED_FORM = true;
void NO_SHARED_FORM;
/**
 * The FR-HA-20 denylist over names: no fillable form id or field code may match (`form-registry.test.ts`; plan-019 GATE
 * may reuse it).
 */
export const NEVER_FILL_CODE_PATTERN = /password|secret|token|iban|card|otp|pin|key|scope/;
/**
 * AMENDED 2026-10-18 (ADR 0072): the words a COMPONENT-registered field's code may not hold as one of its `_`-separated
 * parts — the never-actionable categories by name (a website address, a contact detail, money, people, removal,
 * publishing, consent). A component's field whose code holds one is never registered, whatever its kind
 * (`isComponentFieldCodeAllowed`; the portal's component layer and the help host both ask).
 */
export const NEVER_COMPONENT_CODE_PART = Object.freeze([
    "website", "url", "address", "domain", "email", "phone", "tel", "number", "forward", "contact",
    "fee", "price", "amount", "cost", "budget", "cap", "balance", "payment", "pay", "iban", "card", "vat",
    "invite", "invitation", "member", "role", "owner",
    "delete", "remove", "archive", "close", "cancel", "erase", "publish", "build", "submit",
    "password", "secret", "token", "key", "otp", "pin", "scope", "code", "recovery", "consent", "terms", "recording",
]);
/** The shape of a field code (0153's `help_action.field_code` CHECK). */
const FIELD_CODE_SHAPE = /^[a-z][a-z0-9_]{0,63}$/;
/**
 * Whether a component may register a field under `code` in `formId`: the form takes component fields, the code has a
 * field code's shape, matches nothing of {@link NEVER_FILL_CODE_PATTERN} and holds no part of
 * {@link NEVER_COMPONENT_CODE_PART}, and is not one of the form's registered fields (those are bound by the form).
 */
export function isComponentFieldCodeAllowed(formId, code) {
    const form = HELP_FORM_REGISTRY[formId];
    if (form.componentField !== true || !FIELD_CODE_SHAPE.test(code) || NEVER_FILL_CODE_PATTERN.test(code))
        return false;
    if (Object.hasOwn(form.field, code))
        return false;
    return !code.split("_").some((part) => NEVER_COMPONENT_CODE_PART.includes(part));
}
/** The kinds a component may register a field of (never `opening_hours`, which only its editor binds). */
export const COMPONENT_FILLABLE_KIND = ["text", "single_choice", "local_time", "local_date", "whole_number", "toggle"];
// ── lookup ──────────────────────────────────────────────────────────────────────────────────
const HELP_FORM_ID_SET = new Set(Object.keys(HELP_FORM_REGISTRY));
const NEVER_FILL_FORM_ID_SET = new Set(Object.keys(NEVER_FILL_FORM));
const FILLABLE_KIND_SET = new Set(FILLABLE_KIND);
/** Every fillable form id, in registry order. */
export const HELP_FORM_ID = Object.freeze(Object.keys(HELP_FORM_REGISTRY));
export function isHelpFormId(value) {
    return typeof value === "string" && HELP_FORM_ID_SET.has(value);
}
export function isNeverFillFormId(value) {
    return typeof value === "string" && NEVER_FILL_FORM_ID_SET.has(value);
}
export function isFillableKind(value) {
    return typeof value === "string" && FILLABLE_KIND_SET.has(value);
}
/** The registered form, or `null` for a never-fill form and anything outside the registry. */
export function helpFormOf(value) {
    return isHelpFormId(value) ? HELP_FORM_REGISTRY[value] : null;
}
/** A form's field codes in fill order (registry-declaration order). */
export function helpFormFieldCodes(formId) {
    return Object.keys(HELP_FORM_REGISTRY[formId].field);
}
/** One registered field of a form, or `null`. */
export function helpFormFieldOf(formId, code) {
    if (typeof code !== "string")
        return null;
    const field = HELP_FORM_REGISTRY[formId].field;
    return Object.hasOwn(field, code) ? field[code] : null;
}
/** A component field's spec from what the page reports, or `null` when no component may register it. */
export function componentFieldSpecOf(formId, report) {
    if (!isComponentFieldCodeAllowed(formId, report.code))
        return null;
    if (!COMPONENT_FILLABLE_KIND.includes(report.kind))
        return null;
    const kind = report.kind;
    if (kind === "single_choice")
        return report.option === undefined || report.option.length === 0 ? null : { kind, option: report.option };
    return { kind };
}
/**
 * The registry's half of a fill proposal's validation (design §31.6.3): the form must be registered (a never-fill form
 * is refused `never_fill`, anything else `not_in_registry`); each field must be registered for it — or, for a form that
 * takes component fields, reported by the page as one of its components' (`component`, AMENDED 2026-10-18) — and its
 * value must parse for the field. A repeated code keeps its first value. What the page context says is mounted is the
 * caller's check; so are the binding's `accepts` and the form's own validation.
 */
export function checkFillProposal(formId, field, component = []) {
    if (isNeverFillFormId(formId))
        return { kind: "refused", reason: "never_fill" };
    if (!isHelpFormId(formId))
        return { kind: "refused", reason: "not_in_registry" };
    const componentSpec = new Map();
    for (const report of component) {
        const spec = componentFieldSpecOf(formId, report);
        if (spec !== null && !componentSpec.has(report.code))
            componentSpec.set(report.code, spec);
    }
    const specOf = (code) => helpFormFieldOf(formId, code) ?? (typeof code === "string" ? (componentSpec.get(code) ?? null) : null);
    const proposed = new Map();
    const refused = [];
    for (const one of field) {
        if (specOf(one.code) === null)
            refused.push({ code: one.code, reason: "field_not_registered" });
        else if (!proposed.has(one.code))
            proposed.set(one.code, one.value);
    }
    const accepted = [];
    for (const code of [...helpFormFieldCodes(formId), ...componentSpec.keys()]) {
        if (!proposed.has(code))
            continue;
        const spec = specOf(code);
        const parsed = parseFieldValue(spec, proposed.get(code));
        if (parsed.ok)
            accepted.push({ code, kind: spec.kind, value: parsed.value });
        else
            refused.push({ code, reason: "value_invalid", problem: parsed.reason });
    }
    return { kind: "checked", formId, accepted, refused };
}
//# sourceMappingURL=form-registry.js.map